import Typo from 'consts/typography';
import React, { useMemo } from 'react';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import { BetModel, selectBets, selectCurrentConverter } from 'features/home/homeSlice';
import BigNumber from 'bignumber.js';
import { IMG_ALT, MARKET_SPREADS, WAGERS_PLAYER_PROPS } from 'consts/app';
import { getImg } from 'utils/betUtils';
import { selectLevelPoints, selecteCurrentLevel } from 'redux/teaserSlice';
import { sportHaveDraw } from 'api/mockSportCategory';

interface BetInformationItemProps {
  id: string;
  onDelete: (id: string) => void;
  isTeaser?: boolean;
}

const getBetName = (bet: BetModel | undefined): string => {
  if (!bet) return '';
  if (bet.odd.market.includes('total_goals') || bet.odd.market.includes('total_points'))
    return bet.odd.name;
  if (MARKET_SPREADS.includes(bet.odd.market) || WAGERS_PLAYER_PROPS.includes(bet.odd.market))
    return bet.odd.name;
  return bet.odd.selection;
};

const getBetImg = (bet: BetModel | undefined): string | undefined => {
  if (!bet) return undefined;
  if (bet.odd.market.includes('total_goals') || bet.odd.market.includes('total_points'))
    return undefined;
  if (MARKET_SPREADS.includes(bet.odd.market) || WAGERS_PLAYER_PROPS.includes(bet.odd.market)) {
    return bet.odd.selection === bet.homeTeam
      ? bet.homeTeamLogo
      : bet.odd.selection === bet.awayTeam
      ? bet.awayTeamLogo
      : undefined;
  }
  return bet.odd.selection === bet.homeTeam
    ? bet.homeTeamLogo
    : bet.odd.selection === bet.awayTeam
    ? bet.awayTeamLogo
    : undefined;
};

const getTeaserPoint = (bet: BetModel | undefined, levelPoint: number): string => {
  if (bet?.odd.market === 'point_spread') {
    const betPoint = bet.odd.betPoints as number;
    const result = betPoint + levelPoint;
    return result > 0 ? `+${result.toString()}` : result.toString();
  }
  if (bet?.odd.market === 'total_points') {
    const betPoint = bet.odd.betPoints as number;
    const result =
      bet.odd.selectionLine === 'under' ? betPoint + levelPoint : betPoint - levelPoint;
    return result > 0 ? `+${result.toString()}` : result.toString();
  }
  return '0';
};

const getType = (bet: BetModel | undefined): string => {
  if (!bet) return 'TOTAL POINT';

  if (WAGERS_PLAYER_PROPS.includes(bet.odd.market)) return bet.odd.marketName;

  switch (bet.odd.market) {
    case '1st_half_point_spread':
    case '1st_half_run_line':
    case '1st_period_puck_line':
    case 'first_half_point_spread':
    case 'first_half_run_line':
    case 'first_period_puck_line':
    case 'run_line_after_5_innings':
      return 'First half spread';
    case '1st_half_moneyline':
    case '1st_period_moneyline':
    case 'first_half_money_line':
    case 'first_period_money_line':
    case 'first_5_innings_money_line':
      return 'First half money';
    case '1st_half_total_points':
    case '1st_half_total_runs':
    case '1st_period_total_goals':
    case 'first_half_total_points':
    case 'first_half_total_runs':
    case 'first_period_total_goals':
    case 'first_period_total_goals_over_under':
    case 'first_half_total_points_over_under':
    case 'total_runs_after_5_innings_over_under':
      return 'First half total';
    case 'point_spread':
    case 'run_line':
    case 'puck_line':
      return 'Spread';
    case 'moneyline':
    case 'money_line':
    case 'match_result':
    case 'match_winner':
    case 'fight_result_draw_no_bet_':
      return 'Moneyline';
    default:
      return 'Total point';
  }
};

const BetInformationItem: React.FunctionComponent<BetInformationItemProps> = ({
  id,
  onDelete,
  isTeaser = false,
}) => {
  const { isDesktop, isLargeDesktop } = useAppSelector(platformStates);
  const bets = useAppSelector(selectBets);
  const converter = useAppSelector(selectCurrentConverter);
  const t = useAppTranslation();
  const currentLevel = useAppSelector(selecteCurrentLevel);
  const levelPoints = useAppSelector(selectLevelPoints);

  const level = useMemo(() => {
    return levelPoints && levelPoints[currentLevel];
  }, [levelPoints, currentLevel]);

  const betItem: BetModel | undefined = useMemo(
    () => bets.find((x) => x.odd.id === id),
    [bets, id],
  );

  return (
    <>
      <div className="bet-information__item">
        <div className="bet-information__item__header">
          <div className="bet-information__item__header-content">
            <img alt={IMG_ALT} src={getImg(betItem?.category as string)} width={20} />
            <span
              className={` primary-text-color ${isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7}`}
            >
              {`${betItem?.homeTeam} vs ${betItem?.awayTeam}`}
            </span>
          </div>

          <button onClick={() => onDelete(id)} className="icon-close">
            &#x2718;
          </button>
        </div>
        <div className="bet-information__item__body">
          <div className="info-bet">
            {getBetImg(betItem) && <img src={getBetImg(betItem)} width={30} height={30} />}
            <div className="info-bet-right">
              <span
                className={`primary-text-color ${isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7}`}
              >
                {getBetName(betItem)}
              </span>
              {isTeaser && level && (
                <div className="teaser-point-text">
                  <p className={` ${isDesktop || isLargeDesktop ? Typo.p2 : Typo.p4}`}>
                    {t('teaserBet.points')}:{' '}
                    {betItem?.odd?.betPoints && betItem?.odd?.betPoints > 0
                      ? `+${betItem?.odd.betPoints}`
                      : betItem?.odd.betPoints}{' '}
                    &rArr; {getTeaserPoint(betItem, level?.value)}
                  </p>
                </div>
              )}
              {sportHaveDraw.includes(betItem?.category as string) && (
                <span
                  className={`second-text-color  ${
                    isDesktop || isLargeDesktop ? Typo.p5 : Typo.p7
                  }`}
                >
                  {getType(betItem)}
                </span>
              )}
            </div>
            <div className="number-bet">
              <span className={` ${isDesktop || isLargeDesktop ? Typo.p7 : Typo.p9}`}>
                {converter === 'decimal' ? (
                  <>
                    {betItem?.odd.decimalPrice && (
                      <>
                        {`${betItem?.odd.decimalPrice > 0 ? '+' : ''}${new BigNumber(
                          betItem?.odd.decimalPrice,
                        ).toFixed(2)}`}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {betItem?.odd.price && (
                      <>
                        {`${betItem?.odd.price > 0 ? '+' : ''}${new BigNumber(
                          betItem?.odd.price,
                        ).toFixed(0)}`}
                      </>
                    )}
                  </>
                )}
              </span>
            </div>
          </div>
          {betItem?.gameStatus === 'LIVE' && (
            <div className="live">
              <div className="dot-red"></div>
              <span>{t('live')}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BetInformationItem;
